<template>
    <div v-if="!mobileMenuOpen" class="main-header-banner" :class="{ 'main-header-banner--margin': isImpersonating }">
        <client-only>
            <HeaderBanner v-if="user && isImpersonating" type="error" class="impersonate-header">
                <span>{{ $t('logged_in_as_user', { user: user.email }) }}</span>
                <a href="#" @click="logout"> {{ $t('main_menu.logout') }} </a>
            </HeaderBanner>
        </client-only>
        <HeaderBanner v-if="showOutageBanner" type="warning">
            {{ $t('outage_banner') }}
        </HeaderBanner>
        <HeaderBanner v-show="ownerCampaignBannerShown" closeable-id="owner_campaign" type="success">
            <span>
                {{ $t('banners.owner_campaign.text') }}
                <LocalizedLink :to="{ name: 'sign-up' }" target="_blank">
                    {{ $t('banners.owner_campaign.link_text') }}
                </LocalizedLink>
            </span>
        </HeaderBanner>
        <HeaderBanner v-show="renterCampaignBannerShown" closeable-id="renter_campaign" type="success">
            <span>
                {{ $t('banners.renter_campaign.text') }}
                <a :href="renterCampaignLink" target="_blank">{{ $t('banners.renter_campaign.link_text') }} </a>
            </span>
        </HeaderBanner>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import HeaderBanner from '~/components/layout/HeaderBanner.vue';
import { actionTypes as impersonateActions } from '~/store/impersonate';
import posthogMixin from '~/mixins/posthogMixin';

export default {
    components: { HeaderBanner },
    mixins: [posthogMixin],
    computed: {
        ...mapGetters(['healthStatus', 'mobileMenuOpen', 'user', 'isImpersonating', 'isOwner', 'isFeatureFlagLoaded']),
        showOutageBanner() {
            return this.healthStatus !== 'ok';
        },
        ownerCampaignBannerShown() {
            const rentOutPageName = 'rent-out';
            const isOnRentOutPage = this.$route.name.includes(rentOutPageName);
            const ownerBannerPosthogKey = '2025-07-owner-banner';

            if (!isOnRentOutPage || !['at', 'de'].includes(this.$i18n.locale)) {
                return false;
            }

            if (!this.isFeatureFlagLoaded(ownerBannerPosthogKey)) {
                return false;
            }

            return !this.isExperimentEnabled(ownerBannerPosthogKey);
        },
        renterCampaignBannerShown() {
            const homePageName = 'index';
            const cmsPageName = 'all'; // Search page and brands overview page
            const renterBannerPosthogKey = '2025-07-renter-banner';

            const shouldShowRenterBannerOnThisPage = [homePageName, cmsPageName].some(pageName =>
                this.$route.name.includes(pageName)
            );

            if (!shouldShowRenterBannerOnThisPage || this.isOwner) {
                return false;
            }

            if (!this.isFeatureFlagLoaded(renterBannerPosthogKey)) {
                return false;
            }

            return !this.isExperimentEnabled(renterBannerPosthogKey);
        },
        renterCampaignLink() {
            switch (this.$i18n.locale) {
                case 'de':
                case 'at':
                case 'ch':
                    return 'https://help.mycamper.com/de/articles/362282-gewinne-deine-reise-zuruck-mycamper-sommer-wettbewerb';
                case 'dk':
                    return 'https://help.mycamper.com/da/articles/362282-vind-din-rejse-tilbage-mycampers-sommerkonkurrence';
                case 'fi':
                    return 'https://help.mycamper.com/fi/articles/362282-voita-matkasi-takaisin-mycamperin-kesakampanja';
                case 'fr':
                    return 'https://help.mycamper.com/fr/articles/362282-gagnez-votre-voyage-le-concours-d-ete-mycamper';
                case 'no':
                    return 'https://help.mycamper.com/nb/articles/362282-vinn-reisen-din-tilbake-mycampers-sommerkonkurranse';
                case 'se':
                case 'fi-sv':
                    return 'https://help.mycamper.com/sv/articles/362282-vinn-tillbaka-din-resa-mycampers-sommartavling';
                case 'en':
                default:
                    return 'https://help.mycamper.com/en/articles/362282-terms-and-conditions-mycamper-book-and-win-competition';
            }
        },
    },
    methods: {
        logout() {
            if (this.$route.matched.some(record => record.meta.requiresAuth)) {
                this.$router.push(this.localePath({ path: '/' }));
            }

            this.$authService
                .stopImpersonating()
                .then(response => {
                    const { token, user } = response;
                    const fullToken = `Bearer ${token}`;

                    this.$auth.setUserToken(token, token);
                    this.$auth.setUser(user);
                    // Store the token in local storage
                    localStorage.setItem('auth._token.laravelJWT', fullToken);

                    this.$store.dispatch(impersonateActions.CLEAR);
                    this.$router.push(this.localePath({ path: '/' }));
                })
                .catch(error => {
                    console.error(error);
                    this.$sentry.captureException(error);
                });
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';
$impersonate-header-height: 80px;

.main-header-banner {
    &--margin {
        margin-bottom: $impersonate-header-height;
    }

    .impersonate-header {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        height: $impersonate-header-height;
        padding: 0;
        z-index: 100;
        background-color: $error;
        text-align: center;
        color: $white;
        a {
            margin-left: 16px;
            color: $white;
        }
    }
}
</style>
